import {
  AUTH_GUEST_SOLUTION_ID_KEY,
  AUTH_GUEST_TOKEN_KEY,
  AUTH_GUEST_USER_KEY,
  AUTH_TOKEN_KEY,
  AUTH_TOKEN_UPDATED_EVENT,
  AUTH_USER_KEY,
} from '@/constants/auth-storage'

export interface GuestAuthSession {
  token: string
  solutionId: number
}

export interface ActiveAuthUser {
  id?: number | string
  nickname?: string
  username?: string
  email?: string
}

function readStoredAuthUser(storage: Storage, key: string): ActiveAuthUser | null {
  try {
    const raw = storage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    return parsed as ActiveAuthUser
  } catch {
    return null
  }
}

function emitTokenUpdated(token: string): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(AUTH_TOKEN_UPDATED_EVENT, {
    detail: { token },
  }))
}

export function readGuestAuthSession(): GuestAuthSession | null {
  try {
    const token = String(sessionStorage.getItem(AUTH_GUEST_TOKEN_KEY) ?? '').trim()
    const solutionId = Number(sessionStorage.getItem(AUTH_GUEST_SOLUTION_ID_KEY))
    if (!token || !Number.isSafeInteger(solutionId) || solutionId <= 0) return null
    return { token, solutionId }
  } catch {
    return null
  }
}

export function readActiveAuthUser(): ActiveAuthUser | null {
  return readGuestAuthSession()
    ? readStoredAuthUser(sessionStorage, AUTH_GUEST_USER_KEY)
    : readStoredAuthUser(localStorage, AUTH_USER_KEY)
}

/**
 * 获取当前 JWT 原始串（不含 Bearer 前缀）。
 * 优先读取当前 tab 的 Guest Session，不读 Pinia store，避免状态不同步。
 */
export function resolveAuthTokenRaw(): string {
  const guest = readGuestAuthSession()
  if (guest) return guest.token
  try {
    return String(localStorage.getItem(AUTH_TOKEN_KEY) ?? '').trim()
  } catch {
    return ''
  }
}

export function persistAuthTokenRaw(token: string): void {
  const next = token.trim()
  if (!next) return

  const guest = readGuestAuthSession()
  if (guest) sessionStorage.setItem(AUTH_GUEST_TOKEN_KEY, next)
  else localStorage.setItem(AUTH_TOKEN_KEY, next)
  emitTokenUpdated(next)
}

export function establishGuestAuthSessionRaw(token: string, solutionId: number): void {
  const next = String(token ?? '').trim().replace(/^bearer\s+/i, '')
  const id = Number(solutionId)
  if (!next || !Number.isSafeInteger(id) || id <= 0) {
    throw new Error('临时分享会话无效')
  }
  sessionStorage.setItem(AUTH_GUEST_TOKEN_KEY, next)
  sessionStorage.setItem(AUTH_GUEST_SOLUTION_ID_KEY, String(id))
  sessionStorage.removeItem(AUTH_GUEST_USER_KEY)
  emitTokenUpdated(next)
}

export function clearGuestAuthSessionRaw(): void {
  try {
    sessionStorage.removeItem(AUTH_GUEST_TOKEN_KEY)
    sessionStorage.removeItem(AUTH_GUEST_USER_KEY)
    sessionStorage.removeItem(AUTH_GUEST_SOLUTION_ID_KEY)
  } catch {
    /* ignore */
  }
}
